import React from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import Link from "next/link";
import classNames from "classname";
import BreadcrumbsData from "../variables/variables";

const Breadcrumbs = (props) => {
	const router = useRouter();

	const propsMiasto = props.miasto;
	const cityData = BreadcrumbsData?.miasta?.[propsMiasto];

	if (!cityData) {
		return null;
	}

	const baseUrl = "https://przyjazne-deklaracje.pl";

	const cityName = `Przyjazne Deklaracje ${cityData.name.who}`;
	const cityUrl = cityData.name.url;

	const isGlobalPortal = propsMiasto === "PRZYJAZNEDEKLARACJE";

	const cleanPath = String(router?.asPath || "")
		.split("?")[0]
		.split("#")[0];

	const rawPathParts = cleanPath.split("/").filter(Boolean);

	if (rawPathParts.length === 0) {
		return null;
	}

	const convertBreadcrumb = (value) => {
		const s = String(value).split("?")[0].split("#")[0];

		const breadcrumbText = {
			witryna: cityName,
			kontakt: "Kontakt",
			"mapa-strony": "Mapa strony",
			"deklaracja-dostepnosci": "Deklaracja dostępności",

			"podatek-od-srodkow-transportowych": "Podatek od środków transportowych",
			"dt-1-deklaracja-na-podatek-od-srodkow-transportowych":
				"Deklaracja DT-1 na podatek od środków transportowych",

			"podatek-od-nieruchomosci": "Podatek od nieruchomości",
			"podatek-rolny": "Podatek rolny",
			"podatek-lesny": "Podatek leśny",
			"oplata-za-gospodarowanie-odpadami-komunalnymi":
				"Opłata za gospodarowanie odpadami komunalnymi",

			"dla-mieszkanca": "Dla Mieszkańca",
			"dla-samorzadu": "Dla Samorządu",
			"abc-podatkow": "ABC podatków",
			"pomoc-video": "Pomoc video",
			"jak-to-dziala": "Jak to działa",
			"wazne-terminy": "Ważne terminy",
			aktualnosci: "Aktualności",
			wyszukiwarka: "Wyszukiwarka",

			"deklaracja-dn-1": "Deklaracja DN-1",
			"druk-in-1": "Druk IN-1",
			"jak-otrzymac-numer-pesel": "Jak otrzymać numer PESEL?",

			autorzy: "Autorzy",
			"krystyna-gasiorek": "Krystyna Gąsiorek",

			"najczesciej-zadawane-pytania-faq":
				"Najczęściej zadawane pytania (FAQ)",

			webinar: "Webinar",
		};

		return breadcrumbText[s] ?? cityName;
	};

	const normalizeHref = (parts) => {
		if (!parts.length) {
			return "/";
		}

		return `/${parts.join("/")}/`;
	};

	const buildTrail = (pathParts, hrefPrefixParts = []) => {
		const result = [];

		for (let i = 0; i < pathParts.length; i += 1) {
			const part = pathParts[i];

			if (part === "strona" && pathParts[i + 1]) {
				const pageNumber = pathParts[i + 1];

				result.push({
					breadcrumb: `Strona ${pageNumber}`,
					href: normalizeHref([...hrefPrefixParts, ...pathParts.slice(0, i + 2)]),
				});

				i += 1;
				continue;
			}

			result.push({
				breadcrumb: convertBreadcrumb(part),
				href: normalizeHref([...hrefPrefixParts, ...pathParts.slice(0, i + 1)]),
			});
		}

		return result;
	};

	let breadcrumbs = [];

	if (isGlobalPortal) {
		const rootBreadcrumb = {
			breadcrumb: "Przyjazne Deklaracje",
			href: "/",
		};

		const trail = buildTrail(rawPathParts);

		breadcrumbs = [rootBreadcrumb, ...trail];
	} else {
		const firstPart = rawPathParts[0];

		const isCityPath =
			firstPart === cityUrl ||
			firstPart === propsMiasto ||
			firstPart?.toLowerCase?.() === String(cityUrl).toLowerCase();

		if (!isCityPath) {
			return null;
		}

		const cityRootBreadcrumb = {
			breadcrumb: cityName,
			href: `/${cityUrl}/`,
		};

		const cityPathParts = rawPathParts.slice(1);

		if (cityPathParts.length === 0) {
			return null;
		}

		const trail = buildTrail(cityPathParts, [cityUrl]);

		breadcrumbs = [cityRootBreadcrumb, ...trail];
	}

	if (!breadcrumbs.length || breadcrumbs.length === 1) {
		return null;
	}

	const visibleBreadcrumbs = props.title
		? [
				...breadcrumbs.slice(0, -1),
				{
					...breadcrumbs[breadcrumbs.length - 1],
					breadcrumb: props.title,
				},
		  ]
		: breadcrumbs;

	const jsonLd = {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		itemListElement: visibleBreadcrumbs.map((breadcrumb, index) => ({
			"@type": "ListItem",
			position: index + 1,
			name: breadcrumb.breadcrumb,
			item: `${baseUrl}${breadcrumb.href}`,
		})),
	};

	return (
		<>
			<Head>
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{
						__html: JSON.stringify(jsonLd),
					}}
				/>
			</Head>

			<section
				className={classNames("pd-breadcrumbs", {
					"pd-breadcrumbs--light": props.layout === "light",
					"pd-breadcrumbs--dark": props.layout === "dark",
				})}
			>
				<nav aria-label="Breadcrumbs">
					<ul className="pd-breadcrumbs__list">
						{visibleBreadcrumbs.map((breadcrumb, i) => {
							const isLast = i === visibleBreadcrumbs.length - 1;

							if (isLast) {
								return (
									<li key={`${breadcrumb.href}-${i}`} aria-current="page">
										<span>{breadcrumb.breadcrumb}</span>
									</li>
								);
							}

							return (
								<li key={`${breadcrumb.href}-${i}`}>
									<Link href={breadcrumb.href} aria-label={breadcrumb.breadcrumb}>
										{breadcrumb.breadcrumb}
									</Link>
								</li>
							);
						})}
					</ul>
				</nav>
			</section>
		</>
	);
};

export default Breadcrumbs;